import { ReactElement, useEffect, useState } from 'react';
import { YearSelection } from '../YearSelection';
import { FormContent } from './FormContent';
import { InfoIconCR } from 'icons';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { Form, InfoContainer } from './styles';
import { defaultFormValues } from './constants';
import { DefaultFormValues, FormValues } from './types';
import { FormFooter } from './FormFooter';
import { prepareSetGoalsParams, setEnergyGoals } from '../../helpers';
import { useToast } from 'contexts/newToastContext';
import { SetGoalsResponse } from '../../types';

interface EnergyConsumptionFormProps {
  loading: boolean;
  onCancel?: () => void;
  onSuccess: (responses: SetGoalsResponse[]) => void;
  defaultValues?: DefaultFormValues;
  unitId?: string;
}

function InfoContent(): ReactElement {
  const { t } = useTranslation();
  return (
    <InfoContainer>
      <InfoIconCR />
      <span>{t('metaNaoDefinidaAnoValorMesesAnteriores')}</span>
    </InfoContainer>
  );
}

export function EnergyConsumptionForm({
  defaultValues,
  loading,
  onCancel,
  onSuccess,
  unitId,
}: Readonly<EnergyConsumptionFormProps>): ReactElement {
  const methods = useForm<FormValues>({
    defaultValues: defaultFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [submitLoading, setSubmitLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  async function onSubmit(data: FormValues): Promise<void> {
    if (!unitId) {
      return;
    }

    setSubmitLoading(true);

    const params = prepareSetGoalsParams(data, unitId, defaultValues?.formValues);

    const promises: (() => Promise<SetGoalsResponse | undefined>)[] = [];
    for (const param of params) {
      promises.push(() => setEnergyGoals(param));
    }
    const responses = await Promise.all(promises.map((p) => p()));

    setSubmitLoading(false);

    if (responses.every((response): response is SetGoalsResponse => !!response)) {
      toast(t('sucessoTitulo'), t('sucessoSalvarInformacoes'), 'success');
      onSuccess(responses);
      onCancel?.();
    } else {
      toast(t('erroTitulo'), t('erroSalvarInformacoes'), 'error');
    }
  }

  function onChangeYear(newYear: number): void {
    setYear(newYear);
  }

  useEffect(() => {
    if (defaultValues) {
      setYear(defaultValues.year);
      methods.reset(defaultValues.formValues);
    }
  }, [defaultValues]);

  useEffect(() => () => {
    methods.reset(defaultFormValues);
  }, []);

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        <YearSelection
          currentYear={year}
          onChangeYear={onChangeYear}
          loading={loading || submitLoading}
        />
        <FormContent
          year={year.toString()}
          loading={loading || submitLoading}
        />
        <InfoContent />
        <FormFooter
          defaultValues={defaultValues?.formValues ?? defaultFormValues}
          onCancel={onCancel}
          loading={submitLoading}
          disabledSubmit={!unitId}
        />
      </Form>
    </FormProvider>
  );
}
